/** Strip protocol, path, port, and www. prefix from a pasted URL or domain */
export const normalizeBlocklistDomain = rawValue => {
  if (!rawValue) return '';

  let value = rawValue.trim();
  if (!value) return '';

  value = value.replace(/^https?:\/\//i, '');

  const slashIndex = value.indexOf('/');
  if (slashIndex !== -1) {
    value = value.slice(0, slashIndex);
  }

  const colonIndex = value.indexOf(':');
  if (colonIndex !== -1) {
    value = value.slice(0, colonIndex);
  }

  if (/^www\./i.test(value)) {
    value = value.replace(/^www\./i, '');
  }

  return value.trim();
};

export const isValidBlocklistDomain = value => {
  if (!value) return false;
  const domainRegex = /^(?!-)[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)+$/;
  return domainRegex.test(value);
};

/** Split comma-, space-, semicolon-, or newline-separated input into tokens */
export const parseBlocklistDomainTokens = text =>
  (text || '')
    .split(/[\s,;]+/)
    .map(token => token.trim())
    .filter(Boolean);

/** Normalize and validate pasted/typed blocklist input */
export const parseBlocklistDomainsFromInput = text => {
  const valid = [];
  const invalid = [];
  const seen = new Set();

  for (const token of parseBlocklistDomainTokens(text)) {
    const normalized = normalizeBlocklistDomain(token);
    if (!normalized) continue;

    const key = normalized.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);

    if (isValidBlocklistDomain(normalized)) {
      valid.push(normalized);
    } else {
      invalid.push(token);
    }
  }

  return {valid, invalid};
};

/** CreatableSelect value → plain URL strings (null-safe) */
export const urlsFromSelect = opts =>
  (opts || []).map(u => (typeof u === 'string' ? u : u?.value ?? '')).filter(Boolean);

/** URL strings → CreatableSelect options */
export const urlsToOptions = urls => (urls || []).map(u => ({value: u, label: u}));

/** Snapshot of loaded blocklist for change detection */
export const createSnapshot = (categories = [], urls = []) => ({
  categoryIds: categories.filter(c => c.blocked).map(c => String(c.unique_id)).sort(),
  urls: urls.map(String).sort(),
  nameById: Object.fromEntries(categories.map(c => [String(c.unique_id), c.name || c.unique_id])),
});

/** Order-independent array equality */
export const sameItems = (a, b) =>
  (a || []).map(String).sort().join() === (b || []).map(String).sort().join();

export const BLOCKLIST_CLIPBOARD_V = 1;

/** JSON for optional system clipboard (copy between tabs / machines) */
export function serializeBlocklistClipboard(categoryIds, urls) {
  return JSON.stringify({
    v: BLOCKLIST_CLIPBOARD_V,
    categoryIds: [...(categoryIds || [])].map(String),
    urls: [...(urls || [])].map(String),
  });
}

export function parseBlocklistClipboard(text) {
  try {
    const o = JSON.parse(text);
    if (o?.v !== BLOCKLIST_CLIPBOARD_V || !Array.isArray(o.categoryIds) || !Array.isArray(o.urls))
      return null;
    return {
      categoryIds: o.categoryIds.map(String).filter(Boolean),
      urls: o.urls.map(String).filter(Boolean),
    };
  } catch {
    return null;
  }
}

/** Plain-text domain list for clipboard (one domain per line) */
export function serializeBlocklistDomainsText(urls) {
  return [...(urls || [])].map(String).filter(Boolean).join('\n');
}

/** Parse domains from clipboard text, full blocklist JSON, or delimited plain text */
export function parseBlocklistDomainsClipboard(text) {
  if (!text?.trim()) return [];

  try {
    const o = JSON.parse(text);
    if (o?.v === BLOCKLIST_CLIPBOARD_V && Array.isArray(o.urls)) {
      return o.urls.map(String).filter(Boolean);
    }
  } catch {
    // fall through to plain-text parsing
  }

  return parseBlocklistDomainsFromInput(text).valid;
}

/** Compute added/removed between two sets */
const diff = (prev, next) => ({
  added: next.filter(x => !new Set(prev).has(x)),
  removed: prev.filter(x => !new Set(next).has(x)),
});

const listOrNone = (arr, fn) => (arr.length ? arr.map(fn || (x => x)).join(', ') : 'none');

/** Build ticket summary describing what changed */
export const buildTicketSummary = ({snapshot, selectedCategories, currentUrls, selectedDomains}) => {
  if (!snapshot) return '';
  const cats = diff(snapshot.categoryIds, selectedCategories.map(String));
  const urls = diff(snapshot.urls, currentUrls);
  const dom = Array.isArray(selectedDomains) ? selectedDomains[0] : selectedDomains;
  const domainStr = dom ? `${dom.label || dom.value} (${dom.value})` : 'none';

  return [
    `Categories to block: ${listOrNone(cats.added, id => `${snapshot.nameById[id] || id} (${id})`)}`,
    `Categories to unblock: ${listOrNone(cats.removed, id => `${snapshot.nameById[id] || id} (${id})`)}`,
    `URLs to block: ${listOrNone(urls.added)}`,
    `URLs to unblock: ${listOrNone(urls.removed)}`,
    `Domain: ${domainStr}`,
  ].join('\n\n');
};
